/**
 * Metrics Configuration
 *
 * This file contains the master metrics configuration including:
 * - Metric names, units, categories
 * - Detailed descriptions with Fall Risk Relevance
 * - Organized into 7 categories: TUG Time, Transition, Gait, Balance, Stability, Turning, Joints
 */

export const METRICS_CONFIG = [
  // ============================================================================
  // 1. TUG TIME CATEGORY
  // ============================================================================
  {
    metric: "TUG_Time",
    unit: "seconds",
    category: "tug",
    apiField: "time",
    tugSide: "H",
    tugFront: "M",
    graphReference: "fronttugTime",
    details:
      "Primary metric for global functional mobility and fall risk assessment.",
    fallRiskRelevance:
      "Primary metric for global functional mobility and fall risk assessment.",
  },

  // ============================================================================
  // 2. TRANSITION CATEGORY
  // ============================================================================
  {
    metric: "Sit_to_Stand_Time",
    unit: "seconds",
    category: "transitions",
    apiField: "sitToStandTime",
    tugSide: "H",
    tugFront: "M",
    graphReference: "timeToStandUp",
    details:
      "Indicates lower body strength during vertical weight transfer movements.",
    fallRiskRelevance:
      "Indicates lower body strength during vertical weight transfer movements.",
  },
  {
    metric: "Attempts_to_Rise",
    unit: "count",
    category: "transitions",
    apiField: "attemptsToRise",
    tugSide: "H",
    tugFront: "M",
    graphReference: "attemptsToRise",
    details:
      "Identifies muscle fatigue or coordination failures during chair exits.",
    fallRiskRelevance:
      "Identifies muscle fatigue or coordination failures during chair exits.",
  },
  {
    metric: "Stand_to_Sit_Time",
    unit: "seconds",
    category: "transitions",
    apiField: "standToSitTime",
    tugSide: "H",
    tugFront: "M",
    graphReference: "standToSitTime",
    details: "Reflects eccentric control and balance when lowering into seats.",
    fallRiskRelevance:
      "Reflects eccentric control and balance when lowering into seats.",
  },

  // ============================================================================
  // 3. GAIT CATEGORY
  // ============================================================================
  {
    metric: "Speed",
    unit: "cm/s",
    category: "gait",
    apiField: "speed",
    tugSide: "H",
    tugFront: "M",
    graphReference: "frontgaitSpeed",
    details:
      "Core indicator of walking efficiency and physical frailty status.",
    fallRiskRelevance:
      "Core indicator of walking efficiency and physical frailty status.",
  },
  {
    metric: "Steps_per_Minute",
    unit: "steps/min",
    category: "gait",
    apiField: "cadence",
    tugSide: "M",
    tugFront: "H",
    graphReference: "frontgaitStepPerMinute",
    details:
      "Measures walking rhythm and efficiency of the neuromuscular system.",
    fallRiskRelevance:
      "Measures walking rhythm and efficiency of the neuromuscular system.",
  },
  {
    metric: "Step_Length",
    unit: "centimeters",
    category: "gait",
    apiField: "stepLength",
    tugSide: "H",
    tugFront: "M",
    graphReference: "frontgaitStepLength",
    details: "Directly relates to hip extension range and forward propulsion.",
    fallRiskRelevance:
      "Directly relates to hip extension range and forward propulsion.",
  },
  {
    metric: "Right_Left_Step_Symmetry",
    unit: "percentage (%)",
    category: "gait",
    apiField: "rightLeftStepSymmetry",
    tugSide: "M",
    tugFront: "H",
    graphReference: "rightLeftStepSymmetry",
    details:
      "Detects bilateral imbalances increasing the risk of lateral falls.",
    fallRiskRelevance:
      "Detects bilateral imbalances increasing the risk of lateral falls.",
  },
  {
    metric: "Gait_Smoothness",
    unit: "unitless",
    category: "gait",
    apiField: "gaitSmoothness",
    tugSide: "H",
    tugFront: "L",
    graphReference: "gaitSmoothness",
    details:
      "Captures energy efficiency and minimization of jerky body motions.",
    fallRiskRelevance:
      "Captures energy efficiency and minimization of jerky body motions.",
  },
  {
    metric: "Foot_Placement",
    unit: "meters",
    category: "gait",
    apiField: "footPlacement",
    tugSide: "L",
    tugFront: "H",
    graphReference: "footPlacement",
    details:
      "Base width consistency is essential for maintaining lateral stability.",
    fallRiskRelevance:
      "Base width consistency is essential for maintaining lateral stability.",
  },

  // ============================================================================
  // 4. BALANCE CATEGORY
  // ============================================================================
  {
    metric: "Head_Wobbling",
    unit: "centimeters",
    category: "balance",
    apiField: "headWobble",
    tugSide: "L",
    tugFront: "M",
    graphReference: "frontbalanceHeadWobbling",
    details:
      "Reflects gaze stabilization quality and vestibular system health status.",
    fallRiskRelevance:
      "Reflects gaze stabilization quality and vestibular system health status.",
  },
  {
    metric: "Foot_Clearance",
    unit: "centimeters",
    category: "balance",
    apiField: "footClearance",
    tugSide: "H",
    tugFront: "L",
    graphReference: "footClearance",
    details: "Direct indicator for preventing trips on carpets or thresholds.",
    fallRiskRelevance:
      "Direct indicator for preventing trips on carpets or thresholds.",
  },
  {
    metric: "Shoulder_Balance_LRdeg",
    unit: "degrees (°)",
    category: "balance",
    apiField: "shoulderBalance",
    tugSide: "L",
    tugFront: "H",
    graphReference: "frontShoulderBalance",
    details:
      "Assesses upper trunk alignment and core stability during movement.",
    fallRiskRelevance:
      "Assesses upper trunk alignment and core stability during movement.",
  },
  {
    metric: "Hip_Balance_LRdeg",
    unit: "degrees (°)",
    category: "balance",
    apiField: "hipBalance",
    tugSide: "L",
    tugFront: "H",
    graphReference: "frontHipBalance",
    details:
      "Measures pelvic levelness during dynamic weight-bearing shifts in stance.",
    fallRiskRelevance:
      "Measures pelvic levelness during dynamic weight-bearing shifts in stance.",
  },

  // ============================================================================
  // 5. STABILITY CATEGORY
  // ============================================================================
  {
    metric: "Trunk_Sway",
    unit: "degrees (°)",
    category: "balance",
    apiField: "trunkSway",
    tugSide: "M",
    tugFront: "H",
    graphReference: "trunkSway",
    details:
      "Key metric for identifying core instability and center-of-mass safety.",
    fallRiskRelevance:
      "Key metric for identifying core instability and center-of-mass safety.",
  },
  {
    metric: "Postural_Control",
    unit: "unitless",
    category: "balance",
    apiField: "posturalControl",
    tugSide: "L",
    tugFront: "M",
    graphReference: "posturalControl",
    details:
      "Quantifies proprioceptive feedback loops during quiet or single-leg stance.",
    fallRiskRelevance:
      "Quantifies proprioceptive feedback loops during quiet or single-leg stance.",
  },

  // ============================================================================
  // 6. TURNING CATEGORY
  // ============================================================================
  {
    metric: "Turning_Time",
    unit: "seconds",
    category: "turning",
    apiField: "turningTime",
    tugSide: "H",
    tugFront: "H",
    graphReference: "turningTime",
    details:
      "Captures rotational stability required to navigate narrow spaces safely.",
    fallRiskRelevance:
      "Captures rotational stability required to navigate narrow spaces safely.",
  },
  {
    metric: "Steps_During_Turn",
    unit: "count",
    category: "turning",
    apiField: "stepsDuringTurn",
    tugSide: "M",
    tugFront: "H",
    graphReference: "stepsDuringTurn",
    details:
      "Efficiency of pivot strategy versus high-risk compensatory shuffling steps.",
    fallRiskRelevance:
      "Efficiency of pivot strategy versus high-risk compensatory shuffling steps.",
  },

  // ============================================================================
  // 7. JOINTS CATEGORY
  // ============================================================================
  {
    metric: "Joint_Angles_Knee",
    unit: "degrees (°)",
    category: "turning",
    apiField: "jointAnglesKnee",
    tugSide: "H",
    tugFront: "L",
    graphReference: "jointAnglesKnee",
    details:
      "Vital for shock absorption and adequate limb clearance during gait.",
    fallRiskRelevance:
      "Vital for shock absorption and adequate limb clearance during gait.",
  },
  {
    metric: "Joint_Angles_Hip",
    unit: "degrees (°)",
    category: "turning",
    apiField: "jointAnglesHip",
    tugSide: "H",
    tugFront: "L",
    graphReference: "jointAnglesHip",
    details:
      "Determines the available range for propulsion and reach efficiency.",
    fallRiskRelevance:
      "Determines the available range for propulsion and reach efficiency.",
  },

  // ============================================================================
  // 8. POWER CATEGORY
  // ============================================================================
  {
    metric: "Vertical_Power",
    unit: "W/kg",
    category: "power",
    apiField: "verticalPower",
    tugSide: "L",
    tugFront: "L",
    graphReference: "",
    details:
      "Measures explosive force needed for rapid recovery from stumbles.",
    fallRiskRelevance:
      "Measures explosive force needed for rapid recovery from stumbles.",
  },
  {
    metric: "Braking_Index",
    unit: "index",
    category: "power",
    apiField: "brakingIndex",
    tugSide: "L",
    tugFront: "L",
    graphReference: "",
    details:
      "Assesses quality of eccentric deceleration during descent or sitting.",
    fallRiskRelevance:
      "Assesses quality of eccentric deceleration during descent or sitting.",
  },
  {
    metric: "Propulsive_Strength",
    unit: "N/kg",
    category: "power",
    apiField: "propulsiveStrength",
    tugSide: "L",
    tugFront: "L",
    graphReference: "",
    details:
      "Reflects functional muscle quality for stairs and uphill walking.",
    fallRiskRelevance:
      "Reflects functional muscle quality for stairs and uphill walking.",
  },
  {
    metric: "Change_Velocity",
    unit: "deg/s",
    category: "power",
    apiField: "changeVelocity",
    tugSide: "M",
    tugFront: "H",
    graphReference: "changeVelocity",
    details: "Athletic agility marker for safe, rapid changes in direction.",
    fallRiskRelevance:
      "Athletic agility marker for safe, rapid changes in direction.",
  },
  {
    metric: "Lateral_Latency",
    unit: "ms",
    category: "power",
    apiField: "lateralLatency",
    tugSide: "L",
    tugFront: "H",
    graphReference: "lateralLatency",
    details:
      "Speed of neuromotor reaction to unexpected lateral balance perturbations.",
    fallRiskRelevance:
      "Speed of neuromotor reaction to unexpected lateral balance perturbations.",
  },

  // ============================================================================
  // 9. CONTROL CATEGORY
  // ============================================================================
  {
    metric: "Sway_Confidence",
    unit: "index",
    category: "control",
    apiField: "swayConfidence",
    tugSide: "L",
    tugFront: "L",
    graphReference: "",
    details:
      "Psychological perception of stability during challenging static balance tasks.",
    fallRiskRelevance:
      "Psychological perception of stability during challenging static balance tasks.",
  },
  {
    metric: "Drift_Rate",
    unit: "cm/s",
    category: "control",
    apiField: "driftRate",
    tugSide: "L",
    tugFront: "L",
    graphReference: "",
    details:
      "Measures the creep of center-of-mass during sustained quiet stance.",
    fallRiskRelevance:
      "Measures the creep of center-of-mass during sustained quiet stance.",
  },
  {
    metric: "Rise_Velocity",
    unit: "m/s",
    category: "control",
    apiField: "riseVelocity",
    tugSide: "M",
    tugFront: "L",
    graphReference: "riseVelocity",
    details:
      "Momentum control metric; lead indicator for explosive power loss.",
    fallRiskRelevance:
      "Momentum control metric; lead indicator for explosive power loss.",
  },
  {
    metric: "Joint_Excursion",
    unit: "%",
    category: "control",
    apiField: "jointExcursion",
    tugSide: "H",
    tugFront: "L",
    graphReference: "jointExcursion",
    details:
      "Total functional mobility reserve used during dynamic movement cycles.",
    fallRiskRelevance:
      "Total functional mobility reserve used during dynamic movement cycles.",
  },
  {
    metric: "Joint_Elasticity",
    unit: "coeff",
    category: "control",
    apiField: "jointElasticity",
    tugSide: "L",
    tugFront: "L",
    graphReference: "",
    details:
      "Quality of soft tissue rebound and mechanical movement efficiency.",
    fallRiskRelevance:
      "Quality of soft tissue rebound and mechanical movement efficiency.",
  },
];

/**
 * Returns ordered category config objects for a given reportType ("front" | "side").
 * Each entry has: key (locale key), summaryKey (API summary field), metrics (filtered METRICS_CONFIG).
 * Used by PDF, History, and Summary views as the single source of metric display truth.
 */
export const getTugCategoryConfig = (reportType) => {
  const isSide = reportType === "side";
  const tugField = isSide ? "tugSide" : "tugFront";
  const isVisible = (m) => m[tugField] === "H" || m[tugField] === "M";

  return [
    {
      key: "functional_transitions",
      summaryKey: "transitions",
      metrics: METRICS_CONFIG.filter(
        (m) =>
          isVisible(m) &&
          (m.category === "tug" || m.category === "transitions"),
      ),
    },
    {
      key: "gait_performance",
      summaryKey: "gait",
      metrics: METRICS_CONFIG.filter(
        (m) => isVisible(m) && m.category === "gait",
      ),
    },
    {
      key: isSide ? "balance_postural_control" : "frontal_balance_symmetry",
      summaryKey: "balance",
      metrics: METRICS_CONFIG.filter(
        (m) => isVisible(m) && m.category === "balance",
      ),
    },
    {
      // Joint_Angles_Knee/Hip are stored under category:"turning" in METRICS_CONFIG
      key: isSide ? "turning_joint_mechanics" : "turning",
      summaryKey: "turning",
      metrics: METRICS_CONFIG.filter(
        (m) => isVisible(m) && m.category === "turning",
      ),
    },
    {
      key: "power",
      summaryKey: "power",
      metrics: METRICS_CONFIG.filter(
        (m) => isVisible(m) && m.category === "power",
      ),
    },
    {
      key: "control",
      summaryKey: "control",
      metrics: METRICS_CONFIG.filter(
        (m) => isVisible(m) && m.category === "control",
      ),
    },
  ];
};

/**
 * Helper function to combine details and fall risk relevance into full description
 */
export const getFullDescription = (metric) => {
  const details = (metric.details || "").trim();
  const fallRisk = (metric.fallRiskRelevance || "").trim();

  if (!fallRisk) {
    return details;
  }

  if (!details) {
    return `Fall Risk Relevance: ${fallRisk}`;
  }

  if (details.includes(fallRisk)) {
    return details;
  }

  return `${details} Fall Risk Relevance: ${fallRisk}`;
};
